import { Button, Skeleton } from "antd";
import { 
    ArrowRight, 
    Rocket, 
    BookOpen, 
    MessageCircle, 
    Sparkles,
    Upload,
    CheckCircle2,
    ExternalLink
} from "lucide-react";
import { useRecoilValue } from "recoil";
import { currentProjectState } from "../../recoil/project";
import { Link } from "react-router-dom";
import TemplateAvatar from "../../components/TemplateAvatar";
import { WindowConfig } from "../../types";
import DiscordLogo from "../../assets/discord-logo-white.svg";

const Welcome = () => {
    const currentProject = useRecoilValue(currentProjectState);
    const windowWithType = window as unknown as WindowConfig;

    const handleOpenChat = () => {
        windowWithType.gist.chat("navigate", "newConversation");
    };

    const templates = [
        { 
            name: "strapi-serverless", 
            displayName: "Strapi", 
            description: "Headless CMS",
            path: "strapi-serverless/.microtica/template.yaml" 
        },
        { 
            name: "medusa", 
            displayName: "Medusa", 
            description: "E-commerce",
            path: "medusa-server/.microtica/template.yaml" 
        },
        { 
            name: "nextjs", 
            displayName: "Next.js", 
            description: "React Framework",
            path: "nextjs/.microtica/template.yaml" 
        },
        { 
            name: "nodejs", 
            displayName: "Node.js", 
            description: "Backend Runtime",
            path: "nodejs/.microtica/template.yaml" 
        },
    ];

    if (!currentProject) {
        return (
            <div className="welcome-page">
                <Skeleton active paragraph={{ rows: 8 }} />
            </div>
        );
    }

    return (
        <div className="welcome-page">
            {/* Animated background */}
            <div className="welcome-bg">
                <div className="welcome-bg-gradient" />
                <div className="welcome-bg-orb welcome-bg-orb-1" />
                <div className="welcome-bg-orb welcome-bg-orb-2" />
                <div className="welcome-bg-orb welcome-bg-orb-3" />
                <div className="welcome-bg-grid" />
            </div>

            {/* Content */}
            <div className="welcome-content">
                {/* Hero Section */}
                <div className="welcome-hero">
                    <div className="welcome-hero-badge">
                        <Sparkles size={14} />
                        <span>Welcome aboard!</span>
                    </div>
                    <h1 className="welcome-hero-title">
                        Your cloud journey<br />
                        <span className="welcome-hero-title-accent">starts here</span>
                    </h1>
                    <p className="welcome-hero-subtitle">
                        You're all set up and ready to deploy. Choose your path below to get started.
                    </p>
                </div>

                {/* Progress Steps */}
                <div className="welcome-progress">
                    <div className="welcome-progress-step welcome-progress-step-complete">
                        <div className="welcome-progress-step-icon">
                            <CheckCircle2 size={20} />
                        </div>
                        <div className="welcome-progress-step-content">
                            <span className="welcome-progress-step-label">Project Created</span>
                            <span className="welcome-progress-step-value">{currentProject.name}</span>
                        </div>
                    </div>
                    <div className="welcome-progress-connector welcome-progress-connector-complete" />
                    <div className="welcome-progress-step welcome-progress-step-complete">
                        <div className="welcome-progress-step-icon">
                            <CheckCircle2 size={20} />
                        </div>
                        <div className="welcome-progress-step-content">
                            <span className="welcome-progress-step-label">Environment Ready</span>
                            <span className="welcome-progress-step-value">development</span>
                        </div>
                    </div>
                    <div className="welcome-progress-connector" />
                    <div className="welcome-progress-step welcome-progress-step-current">
                        <div className="welcome-progress-step-icon welcome-progress-step-icon-pulse">
                            <Rocket size={20} />
                        </div>
                        <div className="welcome-progress-step-content">
                            <span className="welcome-progress-step-label">Next Step</span>
                            <span className="welcome-progress-step-value">Deploy your first app</span>
                        </div>
                    </div>
                </div>

                {/* Action Cards */}
                <div className="welcome-actions">
                    {/* Import App Card */}
                    <Link 
                        to={`/projects/${currentProject.id}/templates/import-app?template=https://raw.githubusercontent.com/microtica/templates/master/import-app/template.yaml&import=true`}
                        className="welcome-action-card welcome-action-card-primary"
                    >
                        <div className="welcome-action-card-icon">
                            <Upload size={24} />
                        </div>
                        <div className="welcome-action-card-content">
                            <h3 className="welcome-action-card-title">Import Existing App</h3>
                            <p className="welcome-action-card-description">
                                Connect your Git repository and deploy an existing application
                            </p>
                        </div>
                        <div className="welcome-action-card-arrow">
                            <ArrowRight size={20} />
                        </div>
                    </Link>

                    {/* Templates Section */}
                    <div className="welcome-templates-section">
                        <div className="welcome-templates-header">
                            <span className="welcome-templates-label">Or start with a template</span>
                        </div>
                        <div className="welcome-templates-grid">
                            {templates.map((template) => (
                                <Link
                                    key={template.name}
                                    to={`/projects/${currentProject.id}/templates/${template.name}?template=https://raw.githubusercontent.com/microtica/templates/master/${template.path}`}
                                    className="welcome-template-card"
                                >
                                    <div className="welcome-template-avatar">
                                        <TemplateAvatar name={template.name} size={36} />
                                    </div>
                                    <div className="welcome-template-info">
                                        <span className="welcome-template-name">{template.displayName}</span>
                                        <span className="welcome-template-desc">{template.description}</span>
                                    </div>
                                </Link>
                            ))}
                        </div>
                        <Link 
                            to={`/projects/${currentProject.id}/templates`}
                            className="welcome-templates-explore"
                        >
                            <span>Explore all templates</span>
                            <ArrowRight size={16} />
                        </Link>
                    </div>
                </div>

                {/* Resources Section */}
                <div className="welcome-resources">
                    <h2 className="welcome-resources-title">Resources to help you succeed</h2>
                    <div className="welcome-resources-grid">
                        <a 
                            href="https://docs.microtica.com" 
                            target="_blank" 
                            rel="noreferrer"
                            className="welcome-resource-card"
                        >
                            <div className="welcome-resource-icon welcome-resource-icon-docs">
                                <BookOpen size={20} />
                            </div>
                            <div className="welcome-resource-content">
                                <h4 className="welcome-resource-title">Documentation</h4>
                                <p className="welcome-resource-description">
                                    Learn how to get the most out of Microtica
                                </p>
                            </div>
                            <ExternalLink size={16} className="welcome-resource-arrow" />
                        </a>
                        <a 
                            href="https://discord.gg/ADaFvAsakW" 
                            target="_blank" 
                            rel="noreferrer"
                            className="welcome-resource-card"
                        >
                            <div className="welcome-resource-icon welcome-resource-icon-discord">
                                <img src={DiscordLogo} alt="Discord" />
                            </div>
                            <div className="welcome-resource-content">
                                <h4 className="welcome-resource-title">Join Community</h4>
                                <p className="welcome-resource-description">
                                    Connect with other developers on Discord
                                </p>
                            </div>
                            <ExternalLink size={16} className="welcome-resource-arrow" />
                        </a>
                        <button 
                            onClick={handleOpenChat}
                            className="welcome-resource-card welcome-resource-card-button"
                        >
                            <div className="welcome-resource-icon welcome-resource-icon-chat">
                                <MessageCircle size={20} />
                            </div>
                            <div className="welcome-resource-content">
                                <h4 className="welcome-resource-title">Get Help</h4>
                                <p className="welcome-resource-description">
                                    Chat with our team for personalized support
                                </p>
                            </div>
                            <ArrowRight size={16} className="welcome-resource-arrow" />
                        </button>
                    </div>
                </div>

                {/* Skip Link */}
                <div className="welcome-skip">
                    <Link to={`/projects/${currentProject.id}/environments`}>
                        <Button type="link" size="small">
                            Skip to dashboard
                        </Button>
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default Welcome;
